/*
 * elveth-motion.js — scroll-reveal stagger + section-rendering-api safe.
 * Self-initializes; re-runs on Shopify theme editor section reloads.
 * Public API: window.ElvethMotion.refresh(root?)
 */
(function () {
  'use strict';

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var observer = null;

  function ensureObserver() {
    if (observer || prefersReducedMotion || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    return observer;
  }

  function applyStagger(root) {
    // Default stagger globale: legge --elveth-reveal-stagger dal <body>
    // (impostato dal theme setting). La sezione può sovrascrivere via
    // data-elveth-stagger="120" sul wrapper.
    var bodyVar = parseInt(getComputedStyle(document.body).getPropertyValue('--elveth-reveal-stagger'), 10);
    var globalDefault = isFinite(bodyVar) && bodyVar > 0 ? bodyVar : 90;
    var groups = (root || document).querySelectorAll('[data-elveth-stagger]');
    groups.forEach(function (group) {
      var attrVal = group.getAttribute('data-elveth-stagger');
      var step = (attrVal && parseInt(attrVal, 10)) || globalDefault;
      var children = group.querySelectorAll('[data-elveth-reveal]');
      children.forEach(function (el, i) {
        if (!el.style.getPropertyValue('--elveth-reveal-delay')) {
          el.style.setProperty('--elveth-reveal-delay', i * step + 'ms');
        }
      });
    });
  }

  // ============================================================
  // Auto-tagger: applica data-elveth-reveal a tutti gli elementi testuali
  // dentro le sezioni ELVETH che non lo hanno già. Permette al setting
  // globale (theme settings → Animazioni testi ELVETH) di animare anche
  // le sezioni recenti a blocchi (linktree, newsletter, brand-footer)
  // senza dover modificare ogni singolo template.
  //
  // Idempotente: salta elementi già taggati (anche con data-elveth-reveal=""
  // vuoto). Esclude le sezioni con animation engine proprio (manifesto,
  // curtain-title, manifest words, locanda-stagger).
  // ============================================================
  function autoTagText(root) {
    // Scope: contenitori delle sezioni ELVETH dove vogliamo l'animazione di default.
    var SCOPES = '.elveth-section, .elveth-linktree, .elveth-blog, ' +
                 '.galleria-orizzontale, .restaurant-group-footer, ' +
                 '.restaurant-menu, .testo-galleria, ' +
                 '[data-tg-gsap], [class*="locanda-tre-colonne-"], ' +
                 '.elveth-nl-section, .shopify-section-elveth-linktree';
    // Esclusioni: sezioni con motion engine proprio (no preset globale).
    var EXCLUDE_SECTIONS = '.elveth-manifesto';
    // Elementi target: testo "naturale" delle sezioni.
    var SELECTORS = [
      '.elveth-eyebrow', '.elveth-script',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p',
      '.rgf-eyebrow', '.rgf-text', '.rgf-title',
      '.rgf-label', '.rgf-restaurant-desc',
      '.lt-heading', '.lt-paragraph',
      '.elveth-claim__title', '.elveth-hero__lead'
      /* Rimossi .elveth-cta e .elveth-btn: i bottoni hanno hover/transform
         propri (translateY al hover) che entrerebbero in conflitto con
         il transform di reveal. Buttons restano statici, niente animazione
         d'ingresso — coerente con UX standard. */
    ].join(', ');
    // Esclusioni puntuali (elementi che hanno già un loro motion).
    var EXCLUDE_ELEMENTS = '[data-manifesto-text], [data-curtain-title], ' +
                           '[data-curtain-panel], .elveth-manifesto__word, ' +
                           '[data-elveth-reveal-skip]';

    var scopes = (root || document).querySelectorAll(SCOPES);
    scopes.forEach(function (scope) {
      if (scope.closest(EXCLUDE_SECTIONS)) return;
      var nodes = scope.querySelectorAll(SELECTORS);
      nodes.forEach(function (el) {
        if (el.hasAttribute('data-elveth-reveal')) return;
        if (el.matches(EXCLUDE_ELEMENTS)) return;
        if (el.closest(EXCLUDE_ELEMENTS)) return;
        if (el.closest(EXCLUDE_SECTIONS)) return;
        // Se un ancestor è già tagged con data-elveth-reveal (es. titolo
        // hero che usa mask + span script interno), evita doppia animazione
        // annidata: il parent gestisce già il reveal.
        if (el.parentElement && el.parentElement.closest('[data-elveth-reveal]')) return;
        el.setAttribute('data-elveth-reveal', '');
      });
    });
  }

  function refresh(root) {
    autoTagText(root);
    // Preset "none": nessun reveal di alcun tipo. Forziamo lo stato finale
    // su tutti gli elementi taggati e saltiamo l'observer + le animazioni
    // GSAP testuali (curtain-title, etc.). Il CSS gestisce già lo stato
    // statico via !important ma l'IO observer sarebbe lavoro inutile.
    var globalPreset = document.body && document.body.getAttribute('data-elveth-reveal-preset');
    if (globalPreset === 'none') {
      (root || document).querySelectorAll('[data-elveth-reveal]').forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }
    if (prefersReducedMotion) {
      (root || document).querySelectorAll('[data-elveth-reveal]').forEach(function (el) {
        el.classList.add('is-revealed');
      });
      (root || document).querySelectorAll('.elveth-manifesto__word').forEach(function (el) {
        el.classList.add('is-on');
      });
      (root || document).querySelectorAll('[data-go-stagger] .galleria-orizzontale__image-wrap').forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }
    applyStagger(root);
    var obs = ensureObserver();
    if (!obs) return;
    (root || document).querySelectorAll('[data-elveth-reveal]:not(.is-revealed)').forEach(function (el) {
      obs.observe(el);
    });

    // Galleria orizzontale: stagger reveal clip-mask dal basso (CSS-driven).
    (root || document).querySelectorAll('[data-go-stagger]').forEach(function (track) {
      if (track.dataset.goStaggerInit === '1') return;
      track.dataset.goStaggerInit = '1';
      var wraps = track.querySelectorAll('.galleria-orizzontale__image-wrap');
      if (!wraps.length) return;
      var step = parseInt(track.getAttribute('data-go-stagger-step'), 10) || 180;
      var section = track.closest('.galleria-orizzontale') || track;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          wraps.forEach(function (w, i) {
            setTimeout(function () { w.classList.add('is-revealed'); }, i * step);
          });
          io.unobserve(section);
        });
      }, { threshold: 0.25, rootMargin: '0px 0px -10% 0px' });
      io.observe(section);
    });
  }

  // Highlight "today" in any [data-elveth-today] group (children with data-day).
  // Riconosce: singolo giorno (lun..dom), liste (lun,mar,gio | lun mar gio),
  // range con qualsiasi separatore dash (mar-ven, mer–ven, gio—dom).
  function markToday(root) {
    var dayKeys = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
    var todayKey = dayKeys[new Date().getDay()];
    var todayIdx = dayKeys.indexOf(todayKey);
    var DASH_RE = /[‐-―\-]/; // -, ‐, ‑, ‒, –, —, ―
    (root || document).querySelectorAll('[data-elveth-today]').forEach(function (group) {
      group.querySelectorAll('[data-day]').forEach(function (item) {
        var raw = (item.getAttribute('data-day') || '').toLowerCase().trim();
        var match = false;
        // Estraggo TUTTI i codici giorno presenti nella stringa
        var found = raw.match(/\b(lun|mar|mer|gio|ven|sab|dom)\b/g) || [];

        if (found.length === 1) {
          match = found[0] === todayKey;
        } else if (found.length >= 2 && DASH_RE.test(raw) && found.length === 2) {
          // Range "X-Y"
          var startIdx = dayKeys.indexOf(found[0]);
          var endIdx = dayKeys.indexOf(found[1]);
          if (startIdx > -1 && endIdx > -1) {
            match = (startIdx <= endIdx)
              ? (todayIdx >= startIdx && todayIdx <= endIdx)
              : (todayIdx >= startIdx || todayIdx <= endIdx);
          }
        } else if (found.length > 1) {
          // Lista discreta "lun,gio,sab"
          match = found.indexOf(todayKey) !== -1;
        }

        item.toggleAttribute('data-is-today', match);
      });
    });
  }

  // GSAP scroll-driven effects — un solo motion system per elemento (no conflitti con CSS reveal)
  var _gsapConfigured = false;

  // Helper guard: ritorna true se l'elemento è già stato inizializzato.
  // Usato per evitare creare ScrollTrigger duplicati su re-init (view transitions,
  // bfcache restore, MutationObserver). Setta il flag al primo passaggio.
  function emInit(el, key) {
    var flag = '_em_' + (key || 'init');
    if (el[flag]) return true;
    el[flag] = true;
    return false;
  }

  function initGSAP(root) {
    if (prefersReducedMotion) return;
    if (!window.gsap || !window.ScrollTrigger) return;
    var scope = root || document;
    window.gsap.registerPlugin(window.ScrollTrigger);

    // Config one-time: previene jitter da resize address-bar mobile,
    // performance migliore con limitCallbacks (riduce frequenza onUpdate),
    // smoothness uniforme.
    if (!_gsapConfigured) {
      _gsapConfigured = true;
      window.ScrollTrigger.config({
        ignoreMobileResize: true,
        limitCallbacks: true,
        autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load,resize'
      });
      // Default su tutti i ScrollTrigger: fastScrollEnd salta l'interpolazione
      // intermedia su scroll veloci, atterrando direttamente al frame finale
      // (riduce il jank visibile sulle prime animazioni hero/parallax che
      // normalmente fanno scrub catch-up al primo scroll).
      window.ScrollTrigger.defaults({ fastScrollEnd: true });
      window.gsap.config({ force3D: true, nullTargetWarn: false });
      // Defaults globali per tutti i tween scroll-driven
      window.gsap.defaults({ ease: 'power2.out' });
    }

    // Hero: cinemascope-style parallax + scrubbed kenburns + content fade-out
    // FIX bordo grigio: yPercent ridotto e scale alta per garantire che l'immagine
    // copra SEMPRE il container anche al massimo della traslazione.
    scope.querySelectorAll('.elveth-hero').forEach(function (sec) {
      if (emInit(sec, 'hero')) return;
      var img = sec.querySelector('.elveth-hero__img');
      var inner = sec.querySelector('.elveth-hero__inner');
      // Preset effetto scroll dal section setting (data-hero-fx).
      var fx = sec.getAttribute('data-hero-fx') || 'parallax-soft';
      var trig = { trigger: sec, start: 'top top', end: 'bottom top', scrub: 0.9 };

      if (img && fx !== 'none') {
        img.classList.add('elveth-kenburns--off');
        if (fx === 'parallax-soft') {
          // Default: yPercent + scale lievi. Niente bordi grigi visibili
          // grazie a CSS scale 1.06 sulla classe (vedi elveth.css).
          window.gsap.fromTo(img,
            { yPercent: 0, scale: 1.04 },
            { yPercent: 6, scale: 1.12, ease: 'none', scrollTrigger: trig });
        } else if (fx === 'parallax-strong') {
          // Movimento più marcato. Scale aumentato per evitare clipping
          // bordo superiore con yPercent positivo maggiore.
          window.gsap.fromTo(img,
            { yPercent: 0, scale: 1.08 },
            { yPercent: 14, scale: 1.22, ease: 'none', scrollTrigger: trig });
        } else if (fx === 'zoom-only') {
          // Solo zoom progressivo, niente parallax verticale.
          window.gsap.fromTo(img,
            { scale: 1.04 },
            { scale: 1.20, ease: 'none', scrollTrigger: trig });
        } else if (fx === 'fade-zoom') {
          // L'immagine fade-outa + zoom — effetto "dissolvenza cinematica".
          window.gsap.fromTo(img,
            { scale: 1.04, opacity: 1 },
            { scale: 1.16, opacity: 0.35, ease: 'none', scrollTrigger: trig });
        } else if (fx === 'drift-up') {
          // L'immagine sale lentamente, niente scaling — feel "documentary".
          window.gsap.fromTo(img,
            { yPercent: 4, scale: 1.10 },
            { yPercent: -10, scale: 1.10, ease: 'none', scrollTrigger: trig });
        }
      }

      // Content fade-out — applicato a tutti i preset tranne 'none'
      // (mantiene una transizione coerente del testo durante lo scroll).
      if (inner && fx !== 'none') {
        window.gsap.to(inner, {
          y: -40, opacity: 0.4, ease: 'none',
          scrollTrigger: { trigger: sec, start: 'bottom 80%', end: 'bottom 20%', scrub: 0.8 }
        });
      }
    });

    // Claim media: kenburns scrubbed (sostituisce CSS infinite)
    scope.querySelectorAll('.elveth-claim__img').forEach(function (img) {
      if (emInit(img, 'claim')) return;
      img.classList.add('elveth-kenburns--off');
      window.gsap.fromTo(img,
        { scale: 1.05 },
        {
          scale: 1.14,
          ease: 'none',
          scrollTrigger: {
            trigger: img.closest('.elveth-claim'),
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.8
          }
        });
    });

    // Parallax helper opt-in
    scope.querySelectorAll('[data-elveth-parallax]').forEach(function (el) {
      if (emInit(el, 'parallax')) return;
      var amount = parseFloat(el.getAttribute('data-elveth-parallax')) || 10;
      window.gsap.to(el, {
        yPercent: amount,
        ease: 'none',
        scrollTrigger: {
          trigger: el.closest('section') || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.6
        }
      });
    });

    // Manifesto: rotazione + drop-down dell'immagine sopra al testo.
    // Parte da y: -200, rotation: 0 → arriva a y: 0, rotation: 360 quando il
    // contenuto principale entra in viewport. Poi resta fissa.
    // Usa start/end come funzioni per bypassare il bug View Transitions API
    // che corrompe il calcolo con string-based 'top bottom' / 'top 30%'.
    scope.querySelectorAll('.elveth-manifesto__image--rotate').forEach(function (img) {
      if (emInit(img, 'mfRotate')) return;
      var section = img.closest('.elveth-manifesto') || img;
      window.gsap.fromTo(img,
        { y: -200, rotation: 0 },
        {
          y: 0,
          rotation: 360,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: function () { return section.offsetTop - window.innerHeight; },
            end: function () { return section.offsetTop - window.innerHeight * 0.3; },
            scrub: 1,
            invalidateOnRefresh: true
          }
        }
      );
    });

    // Manifesto: scrub-based reveal legato allo scroll (reversibile su scroll-up).
    // Approccio canonico GSAP: gsap.set + tween con stagger gestiti da timeline.
    // GSAP applica i valori iniziali subito al create — se la sezione è già in
    // viewport al primo paint (back nav, anchor, atterraggio dentro la sezione),
    // le parole sono nello stato corretto al frame 0 senza dover aspettare
    // un tick di scroll.
    scope.querySelectorAll('[data-manifesto-text]').forEach(function (el) {
      if (emInit(el, 'mfText')) return;
      var words = el.querySelectorAll('.elveth-manifesto__word');
      if (!words.length) return;
      var section = el.closest('.elveth-manifesto') || el;
      // CRITICAL FIX: Horizon usa View Transitions API che interferisce con il
      // calcolo di start/end in ScrollTrigger (a volte usa rect.top senza
      // sommare scrollY → start negativi su sezioni già scrollate).
      // Soluzione: usare funzioni per start/end. ScrollTrigger le chiama fresh
      // ad ogni refresh, garantendo posizioni assolute corrette via offsetTop.
      window.gsap.set(words, { opacity: 0.16, transition: 'none' });
      window.gsap.to(words, {
        opacity: 1,
        ease: 'none',
        stagger: { each: 1, from: 'start', ease: 'none' },
        immediateRender: false,
        scrollTrigger: {
          trigger: section,
          start: function () { return section.offsetTop - window.innerHeight * 0.7; },
          end: function () { return section.offsetTop + section.offsetHeight - window.innerHeight * 0.4; },
          scrub: 0.6,
          invalidateOnRefresh: true
        }
      });
    });

    // Image scale & fade scroll: small → 1 → fade out (opt-in via [data-elveth-scrollscale])
    scope.querySelectorAll('[data-elveth-scrollscale]').forEach(function (el) {
      if (emInit(el, 'scrollscale')) return;
      window.gsap.fromTo(el,
        { scale: 0.86, opacity: 0.4 },
        {
          scale: 1, opacity: 1,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top 92%', end: 'top 50%', scrub: 0.6 }
        }
      );
      window.gsap.to(el, {
        opacity: 0.25,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'bottom 70%', end: 'bottom 30%', scrub: 0.6 }
      });
    });

    // ============================================================
    // V8 — Initializers per le 10 nuove sezioni scenografiche
    // ============================================================

    // Cinemascope — pinned parallax img + title fade out on scroll-out
    scope.querySelectorAll('.elveth-cinema').forEach(function (sec) {
      if (emInit(sec, 'cinema')) return;
      var img = sec.querySelector('.elveth-cinema__img');
      var inner = sec.querySelector('.elveth-cinema__inner');
      if (!img) return;
      window.gsap.fromTo(img,
        { yPercent: -8, scale: 1.1 },
        { yPercent: 8, scale: 1.06, ease: 'none',
          scrollTrigger: { trigger: sec, start: tgStart(sec, 1), end: tgEnd(sec, 0), scrub: 0.8, invalidateOnRefresh: true } });
      if (sec.getAttribute('data-cinema-pin') === 'true') {
        window.gsap.to(inner, {
          y: -40, opacity: 0.4, ease: 'none',
          scrollTrigger: { trigger: sec, start: 'bottom 80%', end: 'bottom 20%', scrub: 0.8 }
        });
      }
    });

    // Horizontal scroll — pin section, translate track horizontally
    scope.querySelectorAll('[data-hscroll-pin]').forEach(function (pin) {
      if (emInit(pin, 'hscroll')) return;
      var track = pin.querySelector('[data-hscroll-track]');
      if (!track) return;
      if (window.matchMedia('(max-width: 749px)').matches) return; // mobile = native scroll
      var distance = function () { return Math.max(0, track.scrollWidth - track.clientWidth - 24); };
      window.gsap.to(track, {
        x: function () { return -distance(); },
        ease: 'none',
        scrollTrigger: {
          trigger: pin,
          start: 'top top',
          end: function () { return '+=' + distance(); },
          pin: true,
          scrub: 0.6,
          invalidateOnRefresh: true,
          anticipatePin: 1
        }
      });
    });

    // Stack cards — entry animation (sticky already CSS); add y-fade in
    scope.querySelectorAll('[data-stack-card]').forEach(function (card, i) {
      if (emInit(card, 'stack')) return;
      window.gsap.from(card, {
        y: 60, opacity: 0, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: card, start: 'top 92%', toggleActions: 'play none none reverse' }
      });
    });

    // Curtain — panel reveals image on scroll
    // Trigger MOLTO ritardato: l'animazione deve partire QUANDO l'utente è
    // sulla sezione, non prima. Start quando il top del media è al 40% del
    // viewport (mediamente in vista), end quando il bottom è al 40% (l'utente
    // sta lasciando la sezione). Tutto l'arco di reveal coincide con il viewing.
    scope.querySelectorAll('[data-curtain-panel]').forEach(function (panel) {
      if (emInit(panel, 'curtainP')) return;
      var media = panel.parentElement;
      var img = media.querySelector('.elveth-curtain__img');
      // Trigger sulla SECTION. Calcolo basato su offsetTop+offsetHeight
      // della section (stabile, indipendente da getBoundingClientRect del
      // media interno che può variare con margin-inline negativo del
      // mobile full-bleed). Arc esteso (90% top → 10% bottom viewport)
      // per garantire scroll-distance sufficiente anche quando la sezione
      // su mobile portrait è più piccola del viewport.
      var section = media.closest('.elveth-curtain') || media;
      var trig = {
        trigger: section,
        start: function () { return section.offsetTop - window.innerHeight * 0.9; },
        end: function () { return section.offsetTop + section.offsetHeight - window.innerHeight * 0.1; },
        scrub: 1,
        invalidateOnRefresh: true
      };
      window.gsap.fromTo(panel,
        { scaleY: 1 },
        { scaleY: 0, ease: 'power2.inOut', scrollTrigger: trig });
      if (img) {
        window.gsap.fromTo(img,
          { scale: 1.2 },
          { scale: 1, ease: 'none', scrollTrigger: trig });
      }
    });
    // Curtain title GSAP reveal — skip se preset globale = "none"
    var bodyPresetCT = document.body && document.body.getAttribute('data-elveth-reveal-preset');
    if (bodyPresetCT !== 'none') {
      scope.querySelectorAll('[data-curtain-title]').forEach(function (title) {
        if (emInit(title, 'curtainT')) return;
        var section = title.closest('.elveth-curtain') || title;
        // gsap.fromTo (vs gsap.from): valori espliciti from/to, niente
        // re-cache automatico del from al refresh. immediateRender:false
        // evita di applicare il from al primo paint (l'elemento parte dal
        // suo stato naturale, poi anima al primo trigger).
        // toggleActions cambiato a 'play none none none': niente reverse
        // su scroll-up — l'animazione gira UNA SOLA VOLTA all'entry,
        // niente "refresh" visibile su scroll back/forth.
        // invalidateOnRefresh RIMOSSO: i valori from/to sono fissi
        // (numerici), non layout-dependent → no benefit + flash visivo.
        window.gsap.fromTo(title,
          { y: 40, opacity: 0 },
          {
            y: 0, opacity: 1,
            duration: 1.0,
            ease: 'power3.out',
            immediateRender: false,
            scrollTrigger: {
              trigger: section,
              start: function () { return title.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.5; },
              toggleActions: 'play none none none'
            }
          });
      });
    }

    // Zoom frame — image frame expands from small to fullbleed
    scope.querySelectorAll('.elveth-zoom').forEach(function (sec) {
      if (emInit(sec, 'zoom')) return;
      var pin = sec.querySelector('[data-zoom-pin]');
      var frame = sec.querySelector('[data-zoom-frame]');
      var inner = sec.querySelector('[data-zoom-inner]');
      if (!pin || !frame) return;
      window.gsap.fromTo(frame,
        { width: '60%', height: '60%', borderRadius: '4px' },
        { width: '100%', height: '100%', borderRadius: '0px', ease: 'none',
          scrollTrigger: { trigger: pin, start: 'top top', end: 'bottom bottom', scrub: 0.8 } });
      if (inner) {
        window.gsap.to(inner, {
          opacity: 1, ease: 'none',
          scrollTrigger: { trigger: pin, start: 'top top', end: 'center top', scrub: 0.6,
            onUpdate: function (self) { inner.classList.toggle('is-visible', self.progress > 0.4); } }
        });
      }
    });

    // Asymmetric grid — parallax differenziato per cell
    scope.querySelectorAll('[data-asym-cell]').forEach(function (cell, i) {
      if (emInit(cell, 'asym')) return;
      var depth = (i % 3) * 30 + 10;
      window.gsap.fromTo(cell,
        { y: depth, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.0, ease: 'power3.out',
          delay: (i % 3) * 0.08,
          scrollTrigger: { trigger: cell, start: 'top 90%', toggleActions: 'play none none reverse' } });
    });

    // Image pair — celle che scalano alternate
    scope.querySelectorAll('[data-pair-cell]').forEach(function (cell, i) {
      if (emInit(cell, 'pair')) return;
      var img = cell.querySelector('.elveth-pair__img');
      if (!img) return;
      window.gsap.fromTo(img,
        { scale: 1.12, yPercent: i % 2 === 0 ? -6 : 6 },
        { scale: 1.0, yPercent: 0, ease: 'none',
          scrollTrigger: { trigger: cell, start: 'top bottom', end: 'bottom top', scrub: 0.8 } });
    });

    // Sticky index — count update sul primo elemento visibile
    scope.querySelectorAll('.elveth-sticky').forEach(function (sec) {
      if (emInit(sec, 'sticky')) return;
      var rows = sec.querySelectorAll('[data-sticky-row]');
      var count = sec.querySelector('[data-sticky-count]');
      if (!count || !rows.length) return;
      var total = String(rows.length).padStart(2, '0');
      rows.forEach(function (row) {
        window.ScrollTrigger.create({
          trigger: row,
          start: 'top 50%',
          end: 'bottom 50%',
          onEnter: function () { count.textContent = String(parseInt(row.dataset.rowIndex, 10) + 1).padStart(2, '0') + ' / ' + total; },
          onEnterBack: function () { count.textContent = String(parseInt(row.dataset.rowIndex, 10) + 1).padStart(2, '0') + ' / ' + total; }
        });
        // Image reveal
        var img = row.querySelector('.elveth-sticky__img');
        if (img) {
          window.gsap.fromTo(img,
            { scale: 1.15 }, { scale: 1, ease: 'none',
              scrollTrigger: { trigger: row, start: 'top 90%', end: 'top 30%', scrub: 0.8 } });
        }
      });
    });

    // Fullscreen quote — image parallax + text fade in/out
    scope.querySelectorAll('.elveth-fsquote').forEach(function (sec) {
      if (emInit(sec, 'fsquote')) return;
      var img = sec.querySelector('.elveth-fsquote__img');
      var text = sec.querySelector('[data-fsquote-text]');
      if (img) {
        window.gsap.fromTo(img,
          { scale: 1.08, yPercent: -4 },
          { scale: 1.15, yPercent: 4, ease: 'none',
            scrollTrigger: { trigger: sec, start: tgStart(sec, 1), end: tgEnd(sec, 0), scrub: 0.8, invalidateOnRefresh: true } });
      }
      if (text) {
        window.gsap.from(text, {
          y: 50, opacity: 0, duration: 1.2, ease: 'power3.out',
          scrollTrigger: { trigger: sec, start: 'top 70%', toggleActions: 'play none none reverse' }
        });
      }
    });

    // Split banner — celle laterali scaling soft
    scope.querySelectorAll('[data-splitb-cell]').forEach(function (cell) {
      if (emInit(cell, 'splitb')) return;
      var img = cell.querySelector('.elveth-splitb__img');
      if (!img) return;
      window.gsap.fromTo(img,
        { scale: 1.08 }, { scale: 1, ease: 'none',
          scrollTrigger: { trigger: cell, start: 'top bottom', end: 'bottom top', scrub: 0.8 } });
    });

    // ============================================================
    // testo-galleria — animazioni GSAP differenziate (bg + img + accent)
    // Helper per start/end espressi in percentuali viewport ma calcolati
    // come funzioni → bypass del bug View Transitions API che corrompe
    // i calcoli string-based di ScrollTrigger.
    // ============================================================
    function tgStart(sec, fromTop) {
      return function () { return sec.offsetTop - window.innerHeight * fromTop; };
    }
    function tgEnd(sec, fromBottom) {
      return function () { return sec.offsetTop + sec.offsetHeight - window.innerHeight * fromBottom; };
    }
    scope.querySelectorAll('[data-tg-gsap="true"]').forEach(function (sec) {
      if (emInit(sec, 'tg')) return;
      var bgFx     = sec.getAttribute('data-tg-bg-fx');
      var imgFx    = sec.getAttribute('data-tg-img-fx');
      var accentFx = sec.getAttribute('data-tg-accent-fx');
      var bg       = sec.querySelector('.testo-galleria__bg');
      var bgImg    = bg && bg.querySelector('.testo-galleria__bg-img');
      var mainImg  = sec.querySelector('.testo-galleria__media-main img');
      var accImg   = sec.querySelector('.testo-galleria__media-accent img');

      // BG effects (applicati al wrapper bg o all'img al suo interno)
      var bgTarget = bgImg || bg;
      if (bgTarget && bgFx && bgFx !== 'none') {
        if (bgFx === 'parallax-slow' || bgFx === 'parallax-fast') {
          var depth = bgFx === 'parallax-slow' ? 8 : 18;
          window.gsap.fromTo(bgTarget,
            { yPercent: -depth },
            { yPercent: depth, ease: 'none',
              scrollTrigger: { trigger: sec, start: tgStart(sec, 1), end: tgEnd(sec, 0), scrub: 0.7, invalidateOnRefresh: true } });
        }
        else if (bgFx === 'zoom-out') {
          window.gsap.fromTo(bgTarget,
            { scale: 1.2 },
            { scale: 1, ease: 'none',
              scrollTrigger: { trigger: sec, start: tgStart(sec, 1), end: tgEnd(sec, 0), scrub: 0.8, invalidateOnRefresh: true } });
        }
        else if (bgFx === 'drift-x') {
          // Mobile: spostamento orizzontale aumentato (8% vs 4%) perché su
          // viewport stretti il movimento percepito è proporzionalmente
          // ridotto dalla larghezza dell'immagine bg.
          var driftX = window.matchMedia('(max-width: 749px)').matches ? 8 : 4;
          window.gsap.fromTo(bgTarget,
            { xPercent: -driftX },
            { xPercent: driftX, ease: 'none',
              scrollTrigger: { trigger: sec, start: tgStart(sec, 1), end: tgEnd(sec, 0), scrub: 0.7, invalidateOnRefresh: true } });
        }
      }

      // Image FX (riusabile) — usa la SECTION come trigger comune per main+accento
      // così il delay tra principale e accento è rispettato in modo coerente.
      // Trigger volutamente tardivo (top 60%) per evitare che si apra troppo presto.
      function applyFx(target, fx, opts) {
        if (!target || !fx || fx === 'none') return;
        opts = opts || {};
        var wrap = target.parentElement;
        var trig = sec; // trigger comune
        var startPos = tgStart(sec, 0.6);

        if (fx === 'reveal-up') {
          // Clip-mask DAL BASSO: il contenuto emerge dal bordo inferiore verso l'alto.
          // Forziamo uno stacking context separato sul wrap per garantire che il clip-path
          // funzioni anche su elementi position:absolute (caso .testo-galleria__media-accent).
          if (wrap) {
            window.gsap.set(wrap, { clipPath: 'inset(100% 0 0 0)', willChange: 'clip-path', transform: 'translateZ(0)' });
            window.gsap.to(wrap,
              { clipPath: 'inset(0% 0 0 0)', ease: 'power3.out', duration: 1.2, delay: opts.delay || 0,
                onComplete: function () { window.gsap.set(wrap, { willChange: 'auto' }); },
                scrollTrigger: { trigger: trig, start: startPos, toggleActions: 'play none none reverse' } });
          }
        }
        else if (fx === 'mask') {
          if (wrap) {
            window.gsap.set(wrap, { clipPath: 'inset(0 0 100% 0)', willChange: 'clip-path', transform: 'translateZ(0)' });
            window.gsap.to(wrap,
              { clipPath: 'inset(0 0 0% 0)', ease: 'power3.out', duration: 1.2, delay: opts.delay || 0,
                onComplete: function () { window.gsap.set(wrap, { willChange: 'auto' }); },
                scrollTrigger: { trigger: trig, start: startPos, toggleActions: 'play none none reverse' } });
          }
        }
        else if (fx === 'scale-in') {
          var fromScale = opts.scale || 1.15;
          window.gsap.fromTo(target,
            { scale: fromScale, opacity: 0 },
            { scale: 1, opacity: 1, ease: 'power3.out', duration: 1.2, delay: opts.delay || 0,
              scrollTrigger: { trigger: trig, start: startPos, toggleActions: 'play none none reverse' } });
        }
        else if (fx === 'kenburns') {
          window.gsap.fromTo(target,
            { scale: 1.0, yPercent: 0 },
            { scale: 1.12, yPercent: -4, ease: 'none',
              scrollTrigger: { trigger: wrap, start: function () { return wrap.getBoundingClientRect().top + window.scrollY - window.innerHeight; }, end: function () { return wrap.getBoundingClientRect().top + window.scrollY + wrap.offsetHeight; }, scrub: 0.8, invalidateOnRefresh: true } });
        }
        else if (fx === 'fade-blur') {
          window.gsap.fromTo(target,
            { opacity: 0, filter: 'blur(12px)' },
            { opacity: 1, filter: 'blur(0px)', ease: 'power2.out', duration: 1.2, delay: opts.delay || 0,
              scrollTrigger: { trigger: trig, start: startPos, toggleActions: 'play none none reverse' } });
        }
        else if (fx === 'rotate-in') {
          window.gsap.fromTo(target,
            { rotate: -3, opacity: 0, y: 30 },
            { rotate: 0, opacity: 1, y: 0, ease: 'power3.out', duration: 1.0, delay: opts.delay || 0,
              scrollTrigger: { trigger: trig, start: startPos, toggleActions: 'play none none reverse' } });
        }
        else if (fx === 'drift-up') {
          window.gsap.fromTo(target,
            { yPercent: 6 },
            { yPercent: -6, ease: 'none',
              scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: 0.6 } });
        }
      }

      // Sequencing: la principale (sotto) parte per prima, l'accento (sovrapposto) entra dopo.
      // Se "sync" è attivo, l'accento eredita lo stesso effetto della principale.
      var accentSync = sec.getAttribute('data-tg-accent-sync') === 'true';
      var accentFinalFx = accentSync ? imgFx : accentFx;
      applyFx(mainImg, imgFx, { delay: 0 });
      applyFx(accImg, accentFinalFx, { delay: 1.05, scale: 1.2 });

      // Text column reveal — solo se gsap_text_fx attivo
      var textFx = sec.getAttribute('data-tg-text-fx');
      if (textFx && textFx !== 'none') {
        var textCol = sec.querySelector('.testo-galleria__testo');
        if (textCol) {
          var blocks = textCol.querySelectorAll('.testo-galleria__block-occhiello, .testo-galleria__block-titolo, .testo-galleria__block-paragrafo, .testo-galleria__block-pulsante');
          if (textFx === 'fade-up') {
            window.gsap.fromTo(blocks,
              { y: 28, opacity: 0 },
              { y: 0, opacity: 1, ease: 'power3.out', duration: 0.9, stagger: 0.1,
                scrollTrigger: { trigger: textCol, start: 'top 75%', toggleActions: 'play none none reverse' } });
          }
          else if (textFx === 'stagger') {
            window.gsap.fromTo(blocks,
              { y: 40, opacity: 0, filter: 'blur(8px)' },
              { y: 0, opacity: 1, filter: 'blur(0px)', ease: 'power3.out', duration: 1.1, stagger: 0.18,
                scrollTrigger: { trigger: textCol, start: 'top 70%', toggleActions: 'play none none reverse' } });
          }
          else if (textFx === 'mask-rise') {
            // Wrap each block with overflow hidden + translate inner
            blocks.forEach(function (b, i) {
              if (b.dataset.tgWrapped) return;
              b.dataset.tgWrapped = 'true';
              var orig = b.innerHTML;
              b.innerHTML = '<span class="testo-galleria__mask-inner" style="display:inline-block;">' + orig + '</span>';
              b.style.overflow = 'hidden';
              b.style.display = b.tagName === 'A' ? 'inline-flex' : 'block';
              var inner = b.querySelector('.testo-galleria__mask-inner');
              window.gsap.fromTo(inner,
                { yPercent: 110 },
                { yPercent: 0, ease: 'power3.out', duration: 1.1, delay: i * 0.12,
                  scrollTrigger: { trigger: textCol, start: 'top 70%', toggleActions: 'play none none reverse' } });
            });
          }
          else if (textFx === 'letters') {
            // Per il primo titolo: lettera per lettera
            var heading = textCol.querySelector('.testo-galleria__block-titolo');
            if (heading && !heading.dataset.tgSplit) {
              heading.dataset.tgSplit = 'true';
              var txt = heading.textContent;
              heading.innerHTML = txt.split('').map(function (ch) {
                if (ch === ' ') return '<span class="tg-l" style="display:inline-block;">&nbsp;</span>';
                return '<span class="tg-l" style="display:inline-block;">' + ch + '</span>';
              }).join('');
              var letters = heading.querySelectorAll('.tg-l');
              window.gsap.fromTo(letters,
                { y: 30, opacity: 0 },
                { y: 0, opacity: 1, ease: 'power3.out', duration: 0.6, stagger: 0.02,
                  scrollTrigger: { trigger: textCol, start: 'top 70%', toggleActions: 'play none none reverse' } });
            }
            // Resto dei blocchi: fade-up
            var others = textCol.querySelectorAll('.testo-galleria__block-occhiello, .testo-galleria__block-paragrafo, .testo-galleria__block-pulsante');
            window.gsap.fromTo(others,
              { y: 24, opacity: 0 },
              { y: 0, opacity: 1, ease: 'power3.out', duration: 0.9, stagger: 0.12, delay: 0.4,
                scrollTrigger: { trigger: textCol, start: 'top 70%', toggleActions: 'play none none reverse' } });
          }
        }
      }
    });

    // ============================================================
    // Refresh strategy — 4 trigger non sovrapposti:
    // 1. rAF post-init: DOM montato, primo allineamento dei trigger
    // 2. window.load: tutte le risorse async caricate (font, img, css)
    // 3. document.fonts.ready: hook early per Hijrnotes (titoli script)
    // 4. ResizeObserver: cattura cambi di altezza body runtime (img lazy
    //    che entrano in viewport, contenuti dinamici, font fallback swap)
    //
    // Il per-image load listener è stato rimosso: era ridondante perché
    // ResizeObserver scatta automaticamente al load di ogni immagine
    // (body height cambia), e window.load fa già il sync finale.
    // ============================================================
    requestAnimationFrame(function () { window.ScrollTrigger.refresh(); });

    if (document.readyState !== 'complete' && !window._elvethLoadHooked) {
      window._elvethLoadHooked = true;
      window.addEventListener('load', function () {
        requestAnimationFrame(function () { window.ScrollTrigger.refresh(); });
      });
    }

    // Hook fonts.ready: scatta esattamente quando un font async è
    // disponibile, prima che ResizeObserver registri il cambio di altezza
    // dei titoli che lo usano. Mantiene reveal allineati al primo paint.
    if (document.fonts && document.fonts.ready && !window._elvethFontsHooked) {
      window._elvethFontsHooked = true;
      document.fonts.ready.then(function () {
        requestAnimationFrame(function () { window.ScrollTrigger.refresh(); });
      });
    }

    // ResizeObserver globale su body: refresh quando l'altezza totale della
    // pagina cambia (immagini lazy che entrano in viewport, font async,
    // contenuti che si espandono). Throttled per evitare cascade di refresh.
    if (window.ResizeObserver && !window._elvethResizeHooked) {
      window._elvethResizeHooked = true;
      var lastHeight = 0;
      var refreshScheduled = false;
      var ro = new ResizeObserver(function () {
        var newHeight = document.documentElement.scrollHeight;
        if (Math.abs(newHeight - lastHeight) < 10) return; // ignore micro-changes
        lastHeight = newHeight;
        if (refreshScheduled) return;
        refreshScheduled = true;
        requestAnimationFrame(function () {
          refreshScheduled = false;
          window.ScrollTrigger.refresh();
        });
      });
      ro.observe(document.body);
    }

    // Note: i refresh a intervalli (200/800/1600 ms) sono stati rimossi:
    // ResizeObserver sopra cattura qualsiasi cambio di altezza, e gli hook
    // su window.load + per-image-load + fonts.ready coprono i casi reali.
    // I tre setTimeout erano belt-and-suspenders ridondanti che causavano
    // reflow inutili.
  }

  function init(root) {
    refresh(root);
    markToday(root);
    if (window.gsap && window.ScrollTrigger) {
      initGSAP(root);
    } else {
      // Aspetta che GSAP sia caricato (script defer)
      var tries = 0;
      var t = setInterval(function () {
        tries++;
        if (window.gsap && window.ScrollTrigger) {
          clearInterval(t);
          initGSAP(root);
        } else if (tries > 30) {
          clearInterval(t);
        }
      }, 120);
    }
  }

  // Theme editor: re-init quando le sezioni sono caricate/ricaricate.
  // Killiamo prima i ScrollTrigger agganciati a DOM nodes interni alla
  // sezione (evita memory leak: i trigger non si auto-distruggono quando
  // gli elementi vengono detached). Stessa pulizia su unload.
  function killTriggersInScope(scope) {
    if (!window.ScrollTrigger || !scope) return;
    window.ScrollTrigger.getAll().forEach(function (st) {
      var trigger = st.trigger || (st.vars && st.vars.trigger);
      if (trigger && scope.contains && scope.contains(trigger)) st.kill();
    });
  }
  document.addEventListener('shopify:section:load', function (e) {
    killTriggersInScope(e.target);
    init(e.target);
  });
  document.addEventListener('shopify:section:reorder', function () { init(); });
  document.addEventListener('shopify:section:unload', function (e) {
    killTriggersInScope(e.target);
  });

  // ============================================================
  // Horizon usa View Transitions + Section Hydration per navigazione SPA-like.
  // Quando l'utente clicca un link interno, il DOM viene sostituito ma lo script
  // non si ri-esegue: gli ScrollTrigger restano puntati al vecchio DOM e i
  // nuovi elementi non hanno triggers. Hook su tutti gli eventi rilevanti.
  // ============================================================

  // pageshow fires su load fresco + bfcache restore (back/forward navigation)
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      // Cache restore — kill triggers vecchi e re-init pulito
      if (window.ScrollTrigger) {
        window.ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
      }
      init();
    } else if (window.ScrollTrigger) {
      requestAnimationFrame(function () { window.ScrollTrigger.refresh(true); });
    }
  });

  // popstate: navigazione browser back/forward
  window.addEventListener('popstate', function () {
    setTimeout(function () { init(); }, 100);
  });

  // Horizon view-transitions: il DOM viene sostituito durante la transizione.
  // Hook sui custom events che può emettere il framework.
  ['shopify:page:loaded', 'shopify:page:load', 'page:loaded', 'navigate:complete'].forEach(function (evt) {
    document.addEventListener(evt, function () { init(); });
  });

  // MutationObserver fallback: se vengono aggiunte sezioni ELVETH dopo il
  // primo init (es. soft navigation senza eventi custom), re-init.
  if (window.MutationObserver) {
    var moTimer = null;
    var mo = new MutationObserver(function (mutations) {
      var hasNewSection = mutations.some(function (m) {
        return Array.prototype.some.call(m.addedNodes || [], function (n) {
          return n.nodeType === 1 && (
            n.matches && (n.matches('[data-section-id], section, [class*="elveth-"]')) ||
            n.querySelector && n.querySelector('[data-section-id], [class*="elveth-"]')
          );
        });
      });
      if (!hasNewSection) return;
      clearTimeout(moTimer);
      moTimer = setTimeout(function () { init(); }, 150);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  window.ElvethMotion = { refresh: refresh, markToday: markToday, init: init };
})();
